import { Button, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Input } from "@majitap/ui/arc/input/input";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { dispatchSettingsQuery } from "../../dispatch";
import { messageFor } from "../../error-copy";
import { useFormCheck } from "../../form-check";
import styles from "./settings.module.css";

type Limits = {
  late_after_minutes: number;
  not_accepted_after_minutes: number;
  slow_step_minutes: number;
  arrival_radius_m: number;
};
type Settings = Limits & { asap_promise_minutes: number | null };

const MINUTES = { unit: "minutes", min: 1, max: 1440 } as const;
const FIELDS: { key: keyof Limits; label: string; help: string; unit: string; min: number; max: number }[] = [
  {
    key: "late_after_minutes",
    label: "Running late after",
    help: "Minutes past its time (or since it was ordered, with no time) that a delivery not yet on its way is late.",
    ...MINUTES,
  },
  {
    key: "not_accepted_after_minutes",
    label: "Not accepted after",
    help: "Minutes an assigned driver has to accept a delivery before it needs a look.",
    ...MINUTES,
  },
  {
    key: "slow_step_minutes",
    label: "Slow at a step after",
    help: "Minutes a delivery can stay at one step, from accepted to at the customer, before it needs a look.",
    ...MINUTES,
  },
  {
    key: "arrival_radius_m",
    label: "Arrival distance",
    help: "How near the truck must be for the driver to mark arriving at the fill site, arriving at the customer, or delivered.",
    unit: "metres",
    min: 20,
    max: 5000,
  },
];

/**
 * Dispatch alerts (P8-53, Settings › Operations): when the Dispatch page flags a delivery as
 * running late, not accepted or slow at a step (each 1 to 1,440 minutes), and how near a truck must be
 * to mark an arrival (P12-26, 20 m to 5 km). Also the ASAP
 * promise (P15-14): how soon an unscheduled delivery is promised, for on-time reports.
 */
export function DispatchAlertsPage(): ReactNode {
  const settings = useQuery(dispatchSettingsQuery);
  const header = (
    <PageHeader
      title="Dispatch alerts"
      description="When the Dispatch page flags a delivery (running late, not accepted, slow at a step), how near drivers must be to mark that they've arrived, and how soon a delivery with no time is promised."
    />
  );
  if (settings.status === "pending")
    return (
      <>
        {header}
        <PageLoading label="Loading the alerts" />
      </>
    );
  if (settings.status === "error")
    return (
      <>
        {header}
        <PageError message={messageFor(errorOf(settings.error))} onRetry={() => settings.refetch()} />
      </>
    );
  return (
    <>
      {header}
      <LimitsForm current={settings.data} />
    </>
  );
}

/** The ASAP promise: blank for none, else 1 minute to a week. */
const ASAP_MAX = 10080;

function LimitsForm({ current }: { current: Settings }) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState(() => Object.fromEntries(FIELDS.map((f) => [f.key, String(current[f.key])])));
  const [asap, setAsap] = useState(current.asap_promise_minutes === null ? "" : String(current.asap_promise_minutes));
  // number-ok: minutes and metres, not money
  const parsed = Object.fromEntries(FIELDS.map((f) => [f.key, Number.parseInt(values[f.key] ?? "", 10)])) as Limits;
  // number-ok: minutes, not money
  const asapMinutes = asap.trim() === "" ? null : Number.parseInt(asap, 10);
  const asapValid = asapMinutes === null || (/^\d+$/.test(asap.trim()) && asapMinutes >= 1 && asapMinutes <= ASAP_MAX);
  const limitsValid = FIELDS.every(
    (f) => /^\d+$/.test(values[f.key] ?? "") && parsed[f.key] >= f.min && parsed[f.key] <= f.max,
  );
  const valid = limitsValid && asapValid;
  const check = useFormCheck();
  // the dispatch_settings CHECKs, and DispatchSettingsBody in modules/dispatch/src/schemas.ts
  const errors: Record<string, string | undefined> = {
    ...Object.fromEntries(
      FIELDS.map((f) => [
        f.key,
        /^\d+$/.test(values[f.key] ?? "") && parsed[f.key] >= f.min && parsed[f.key] <= f.max
          ? undefined
          : `Enter whole ${f.unit} from ${f.min} to ${f.max.toLocaleString("en")}.`,
      ]),
    ),
    asap: asapValid ? undefined : "Enter whole minutes from 1 to 10,080 (a week), or leave it blank.",
  };
  const changed = FIELDS.some((f) => parsed[f.key] !== current[f.key]) || asapMinutes !== current.asap_promise_minutes;
  const save = useMutation({
    mutationFn: () =>
      ok(api.PUT("/v1/admin/dispatch/settings", { body: { ...parsed, asap_promise_minutes: asapMinutes } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: dispatchSettingsQuery.queryKey });
      // the board's flags follow the new limits
      await queryClient.invalidateQueries({ queryKey: ["dispatch-board"] });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    check.submit(errors, () => {
      if (changed) save.mutate();
    });
  };
  return (
    <form className={`${styles.panel} ${styles.narrow}`} onSubmit={submit} aria-label="Alert limits">
      {FIELDS.map((f) => (
        <Input
          key={f.key}
          label={`${f.label} (${f.unit})`}
          description={f.help}
          inputMode="numeric"
          value={values[f.key]}
          onChange={(e) => {
            save.reset();
            setValues((v) => ({ ...v, [f.key]: e.target.value }));
          }}
          error={check.show(errors[f.key])}
        />
      ))}
      <Input
        label="Promise a delivery with no time within (minutes)"
        description="For on-time reports: an order for as soon as possible is promised this long after it was placed. Leave it blank to promise no time. A scheduled delivery is promised from its time until the running-late limit."
        inputMode="numeric"
        value={asap}
        onChange={(e) => {
          save.reset();
          setAsap(e.target.value);
        }}
        error={check.show(errors.asap)}
      />
      {save.error ? (
        <Alert tone="danger" title="Not saved">
          {messageFor(errorOf(save.error))}
        </Alert>
      ) : null}
      {save.isSuccess && !changed ? (
        <Alert tone="success" title="Saved">
          The Dispatch page and the drivers' app use these limits now. A new promise applies to deliveries placed from
          now on.
        </Alert>
      ) : null}
      <div className={styles.footer}>
        {/* nothing to save while every limit is valid and as saved */}
        <Button type="submit" disabled={valid && !changed} loading={save.isPending}>
          Save alert limits
        </Button>
      </div>
    </form>
  );
}
